import { useMemo } from 'react';
import { Area, CartesianGrid, ComposedChart, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { TrendingUp } from 'lucide-react';
import { BRAND } from '@/lib/brand';
import { cn } from '@/lib/utils';
import { evaluateForecast, type ForecastView } from '@/features/intelligence/dashboard/evaluate';
import { currentMonthKey, formatAxis, formatBucket, formatUnit } from '@/features/intelligence/dashboard/series';
import { FORECAST_METHOD_LABELS, type ForecastWidget } from '@/features/intelligence/dashboard/types';
import { useDashboard, useSourceState } from '../DashboardContext';
import { ChartTooltipContent, LegendRow, WidgetError, WidgetMessage, WidgetSkeleton, type TooltipSeriesInfo } from '../shared';

const FORECAST_COLOR = BRAND.atmosphereDark;
const MANUAL_COLOR = BRAND.freshSalmonDark;
const EXTERNAL_COLOR = BRAND.brightMossDark;

type Row = {
  t: string;
  actual: number | null;
  partial: number | null;
  forecast: number | null;
  band: [number, number] | null;
  manual: number | null;
  external: number | null;
  externalBand: [number, number] | null;
};

function buildRows(view: ForecastView, showBand: boolean): Row[] {
  const byMonth = new Map<string, Row>();
  const row = (t: string): Row => {
    let r = byMonth.get(t);
    if (!r) {
      r = { t, actual: null, partial: null, forecast: null, band: null, manual: null, external: null, externalBand: null };
      byMonth.set(t, r);
    }
    return r;
  };
  for (const p of view.history) row(p.t).actual = p.v;
  const last = view.history[view.history.length - 1];
  // Anchor the forecast line to the last actual so the two meet.
  if (last) row(last.t).forecast = last.v;
  for (const p of view.forecast) {
    const r = row(p.t);
    r.forecast = p.v;
    if (showBand) r.band = [p.lo, p.hi];
  }
  if (view.partial) row(view.partial.t).partial = view.partial.v;
  for (const p of view.manual) row(p.t).manual = p.v;
  for (const p of view.external) {
    const r = row(p.t);
    r.external = p.v;
    if (showBand) r.externalBand = [p.lo, p.hi];
  }
  return Array.from(byMonth.values()).sort((a, b) => a.t.localeCompare(b.t));
}

export function ForecastWidgetView({ widget }: { widget: ForecastWidget }) {
  const { bag, catalog, forecast, isMobile } = useDashboard();
  const { loading, error } = useSourceState([widget.source]);
  const view = useMemo(() => evaluateForecast(widget, bag, forecast, catalog), [widget, bag, forecast, catalog]);

  if (error) return <WidgetError message={error} />;
  if (view.unavailable) return <WidgetMessage icon={TrendingUp} title="Can't forecast yet" detail={view.unavailable} />;
  if (loading || !view.ready) return <WidgetSkeleton variant="chart" />;

  const rows = buildRows(view, widget.showBand);
  const unit = view.unit;
  const current = currentMonthKey();
  const infoByKey: Record<string, TooltipSeriesInfo> = {
    actual: { label: 'Actual', unit, color: BRAND.humus },
    partial: { label: 'Month to date', unit, color: BRAND.oldGrowth },
    forecast: { label: 'Forecast', unit, color: FORECAST_COLOR },
    band: { label: 'Likely range', unit, color: FORECAST_COLOR, range: true },
    manual: { label: view.manualLabel, unit, color: MANUAL_COLOR },
    external: { label: view.externalLabel, unit, color: EXTERNAL_COLOR },
    externalBand: { label: `${view.externalLabel} range`, unit, color: EXTERNAL_COLOR, range: true },
  };
  const dense = isMobile || widget.w <= 4;
  const showSummary = widget.h >= 3;
  const itemLabel = widget.itemId ? bag.items?.find((i) => i.id === widget.itemId) : null;

  return (
    <div className="flex h-full flex-col">
      {itemLabel && (
        <div className="pb-1 text-xs text-muted-foreground">
          {itemLabel.sku} · {itemLabel.name}
        </div>
      )}
      <div className="min-h-0 flex-1">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid stroke={BRAND.linenDark} vertical={false} />
            <XAxis
              dataKey="t"
              stroke={BRAND.oldGrowth}
              fontSize={11}
              tickLine={false}
              minTickGap={dense ? 28 : 16}
              tickFormatter={(v: string) => formatBucket(v, 'month')}
            />
            <YAxis
              stroke={BRAND.oldGrowth}
              fontSize={11}
              width={dense ? 40 : 52}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v: number) => formatAxis(v, unit)}
              domain={view.isBalance ? ['auto', 'auto'] : [0, 'auto']}
            />
            <Tooltip cursor={{ stroke: BRAND.linenDark }} content={<ChartTooltipContent grain="month" infoByKey={infoByKey} />} />
            <ReferenceLine x={current} stroke={BRAND.oldGrowth} strokeDasharray="3 3" strokeOpacity={0.6} />
            {view.isBalance && <ReferenceLine y={0} stroke={BRAND.freshSalmonDark} strokeOpacity={0.5} />}
            {widget.showBand && (
              <Area
                type="monotone"
                dataKey="band"
                stroke="none"
                fill={FORECAST_COLOR}
                fillOpacity={0.12}
                connectNulls={false}
                isAnimationActive={false}
                activeDot={false}
              />
            )}
            {widget.showBand && view.external.length > 0 && (
              <Area
                type="monotone"
                dataKey="externalBand"
                stroke="none"
                fill={EXTERNAL_COLOR}
                fillOpacity={0.1}
                connectNulls={false}
                isAnimationActive={false}
                activeDot={false}
              />
            )}
            <Line type="monotone" dataKey="actual" stroke={BRAND.humus} strokeWidth={2} dot={false} activeDot={{ r: 4 }} connectNulls isAnimationActive={false} />
            <Line
              type="monotone"
              dataKey="forecast"
              stroke={FORECAST_COLOR}
              strokeWidth={2}
              dot={{ r: 2.5, strokeWidth: 0, fill: FORECAST_COLOR }}
              activeDot={{ r: 4 }}
              connectNulls
              isAnimationActive={false}
            />
            {view.manual.length > 0 && (
              <Line
                type="monotone"
                dataKey="manual"
                stroke={MANUAL_COLOR}
                strokeWidth={1.5}
                strokeDasharray="5 4"
                dot={false}
                connectNulls
                isAnimationActive={false}
              />
            )}
            {view.external.length > 0 && (
              <Line
                type="monotone"
                dataKey="external"
                stroke={EXTERNAL_COLOR}
                strokeWidth={1.5}
                strokeDasharray="2 3"
                dot={false}
                connectNulls
                isAnimationActive={false}
              />
            )}
            {view.partial && (
              <Line
                type="monotone"
                dataKey="partial"
                stroke={BRAND.oldGrowth}
                strokeWidth={0}
                dot={{ r: 4, stroke: BRAND.oldGrowth, strokeWidth: 2, fill: '#fff' }}
                activeDot={{ r: 5 }}
                isAnimationActive={false}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <LegendRow
        className="pt-2"
        items={[
          { label: 'Actual', color: BRAND.humus },
          { label: 'Forecast', color: FORECAST_COLOR },
          ...(view.manual.length ? [{ label: view.manualLabel, color: MANUAL_COLOR, dashed: true }] : []),
          ...(view.external.length ? [{ label: view.externalLabel, color: EXTERNAL_COLOR, dashed: true }] : []),
        ]}
      />
      {showSummary && <ForecastSummary view={view} dense={dense} />}
    </div>
  );
}

function ForecastSummary({ view, dense }: { view: ForecastView; dense: boolean }) {
  const { summary, unit } = view;
  const method = view.method ? FORECAST_METHOD_LABELS[view.method] : '—';
  const nextLabel = view.isBalance ? 'This month end' : 'This month';
  const endLabel = view.isBalance ? `In ${view.horizon} mo` : `${view.horizon}-mo total`;
  const endValue = view.isBalance ? summary.endOfHorizon : summary.horizonTotal;
  return (
    <div className={cn('mt-2 grid gap-x-3 gap-y-1 border-t border-border pt-2 text-xs', dense ? 'grid-cols-2' : 'grid-cols-4')}>
      <Stat label="Last month" value={formatUnit(summary.last, unit, { compact: true })} />
      <Stat label={nextLabel} value={formatUnit(summary.next, unit, { compact: true })} accent />
      <Stat label={endLabel} value={formatUnit(endValue, unit, { compact: true })} />
      {!dense && (
        <Stat
          label={summary.zeroCrossing ? 'Cash out' : 'Model'}
          value={summary.zeroCrossing ? formatBucket(summary.zeroCrossing, 'month') : method.replace(/\s*\(.*\)$/, '')}
          warn={!!summary.zeroCrossing}
          hint={view.mape != null ? `±${Math.round(view.mape * 100)}% backtest error · ${summary.fittedMonths} mo fitted` : `${summary.fittedMonths} mo fitted`}
        />
      )}
    </div>
  );
}

function Stat({ label, value, accent, warn, hint }: { label: string; value: string; accent?: boolean; warn?: boolean; hint?: string }) {
  return (
    <div className="min-w-0" title={hint}>
      <div className="truncate text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={cn('truncate font-medium tabular-nums', warn ? 'text-fresh-salmon-dark' : accent ? 'text-atmosphere-dark' : 'text-foreground')}>{value}</div>
    </div>
  );
}
